import React, { useState, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  useTheme,
  alpha,
} from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ExploreIcon from "@mui/icons-material/Explore";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";

import { useMyActiveCirclesQuery, useGetStatusQuery } from "components/Slices/usersApiSlice";
import { useGroupRequestMutation } from "components/Slices/circleApiSlice";
import { useGetStripeQuery } from "components/Slices/accountsApiSlice";
import { MoneyLoading } from "components/routers/MoneyLoading";
import GroupAgreementModal from "../shared/Agreement";
import { PublicCirclesTab } from "../PublicCircles";

import { GroupSection } from "./components";
import type { TabValue, DecisionType } from "./types";

const ViewGroupImproved: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState<TabValue>("my-circles");
  const [open, setOpen] = useState(false);
  const [circleId, setCircleId] = useState<string>("");
  const [bankBannerDismissed, setBankBannerDismissed] = useState(() => {
    return sessionStorage.getItem("bankBannerDismissed") === "true";
  });

  const handleDismissBankBanner = () => {
    setBankBannerDismissed(true);
    sessionStorage.setItem("bankBannerDismissed", "true");
  };

  const { data: stripeAccount } = useGetStripeQuery();
  const { data: userStatus } = useGetStatusQuery();
  const { data: myActiveGroups, isLoading } = useMyActiveCirclesQuery({ status: "active" });
  const { data: pendingGroups, isLoading: isPendingLoading } = useMyActiveCirclesQuery({ status: "pending" });
  const { data: completedGroups, isLoading: isCompletedLoading } = useMyActiveCirclesQuery({ status: "completed" });

  const hasNoBankAccount =
    stripeAccount &&
    (!stripeAccount.externalAccounts || stripeAccount.externalAccounts.length === 0);
  const isVerified = userStatus?.userStatus?.toLowerCase() === "active";
  const hasActiveCircles = (myActiveGroups?.length ?? 0) > 0;
  const showBankBanner = hasNoBankAccount && (hasActiveCircles || !bankBannerDismissed);
  const showVerificationBanner = !isVerified && hasActiveCircles;

  const [groupRequest] = useGroupRequestMutation();

  const sendDecision = useCallback(
    async (groupId: string, decision: DecisionType) => {
      try {
        await groupRequest({ groupId, decision }).unwrap();
        navigate("/");
      } catch (err) {
        console.log({ err });
      }
    },
    [groupRequest, navigate]
  );

  const handleViewDetails = useCallback((groupId: string) => {
    navigate(`/circles/${groupId}`);
  }, [navigate]);

  const handleJoinCircle = useCallback((id: string) => {
    setCircleId(id);
    setOpen(true);
  }, []);

  const handleDeclineCircle = useCallback(
    (id: string) => sendDecision(id, "rejected"),
    [sendDecision]
  );

  const handleAgree = useCallback(() => {
    setOpen(false);
    sendDecision(circleId, "accepted");
  }, [circleId, sendDecision]);

  if (isLoading || isPendingLoading || isCompletedLoading) {
    return <MoneyLoading />;
  }

  const activeCount    = myActiveGroups?.length   ?? 0;
  const pendingCount   = pendingGroups?.length    ?? 0;
  const completedCount = completedGroups?.length  ?? 0;

  const statTiles = [
    { label: "Active",    count: activeCount,    color: theme.palette.success.main },
    { label: "Pending",   count: pendingCount,   color: theme.palette.warning.main },
    { label: "Completed", count: completedCount, color: theme.palette.primary.main },
  ];

  const tabItems: { value: TabValue; label: string; Icon: React.ElementType }[] = [
    { value: "my-circles", label: "My Circles", Icon: GroupIcon },
    { value: "discover",   label: "Discover",   Icon: ExploreIcon },
  ];

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", px: { xs: 2, sm: 3 }, py: { xs: 3, sm: 4 } }}>

      {/* Page header */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 4, gap: 2 }}>
        <Box>
          <Typography
            sx={{
              fontSize: { xs: "1.5rem", sm: "1.85rem" },
              fontWeight: 800,
              color: "text.primary",
              letterSpacing: "-0.02em",
              lineHeight: 1.2,
            }}
          >
            Circles
          </Typography>
          <Typography sx={{ fontSize: "0.9rem", color: "text.secondary", mt: 0.5 }}>
            Manage your lending circles and discover new ones.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => navigate("/create-group")}
          sx={{
            fontWeight: 700,
            fontSize: "0.875rem",
            borderRadius: "10px",
            py: 1.25,
            px: 2.5,
            flexShrink: 0,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          }}
        >
          New Circle
        </Button>
      </Box>

      {/* Bank warning banner */}
      {showBankBanner && (
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "flex-start",
            borderRadius: 3,
            bgcolor: alpha(theme.palette.warning.main, 0.07),
            border: "1px solid",
            borderColor: alpha(theme.palette.warning.main, 0.22),
            p: 2.5,
            mb: 3,
          }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              bgcolor: alpha(theme.palette.warning.main, 0.12),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              mt: 0.1,
            }}
          >
            <WarningAmberIcon sx={{ fontSize: "1rem", color: "warning.dark" }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: "text.primary" }}>
              Add a bank account to receive payouts
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", color: "text.secondary", mt: 0.25, mb: 1.5 }}>
              You'll need a linked bank account before you can receive your circle payout.
            </Typography>
            <Button
              size="small"
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate("/settings?tab=finances")}
              sx={{
                bgcolor: "warning.main",
                "&:hover": { bgcolor: "warning.dark" },
                fontSize: "0.78rem",
                fontWeight: 600,
                borderRadius: "8px",
                color: "#fff",
                boxShadow: "none",
                textTransform: "none",
              }}
            >
              Add Bank Account
            </Button>
          </Box>
          {!hasActiveCircles && (
            <IconButton size="small" onClick={handleDismissBankBanner} sx={{ color: "text.secondary", mt: -0.25 }}>
              <CloseIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          )}
        </Box>
      )}

      {/* Verification warning — shown when in active circles and not verified */}
      {showVerificationBanner && (
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "flex-start",
            borderRadius: 3,
            bgcolor: alpha(theme.palette.error.main, 0.06),
            border: "1px solid",
            borderColor: alpha(theme.palette.error.main, 0.2),
            p: 2.5,
            mb: 3,
          }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              bgcolor: alpha(theme.palette.error.main, 0.1),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              mt: 0.1,
            }}
          >
            <WarningAmberIcon sx={{ fontSize: "1rem", color: "error.main" }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: "text.primary" }}>
              Verify your identity before your payout turn
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", color: "text.secondary", mt: 0.25, mb: 1.5 }}>
              You're in an active circle but haven't verified your identity. You'll be removed before your payout if not completed in time.
            </Typography>
            <Button
              size="small"
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate("/onboarding?step=3")}
              sx={{
                bgcolor: "error.main",
                "&:hover": { bgcolor: "error.dark" },
                fontSize: "0.78rem",
                fontWeight: 600,
                borderRadius: "8px",
                color: "#fff",
                boxShadow: "none",
                textTransform: "none",
              }}
            >
              Verify Identity
            </Button>
          </Box>
        </Box>
      )}

      {/* Stats row (my circles tab only) */}
      {activeTab === "my-circles" && (
        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
          {statTiles.map(({ label, count, color }) => (
            <Box
              key={label}
              sx={{
                flex: "1 1 120px",
                borderRadius: 3,
                border: "1px solid",
                borderColor: alpha(color, 0.18),
                bgcolor: alpha(color, 0.06),
                px: 2.5,
                py: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  color: "text.secondary",
                  mb: 0.5,
                }}
              >
                {label}
              </Typography>
              <Typography sx={{ fontSize: "1.75rem", fontWeight: 800, color: "text.primary", lineHeight: 1 }}>
                {count}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Segmented tab control */}
      <Box
        sx={{
          display: "flex",
          gap: 0.75,
          p: 0.75,
          bgcolor: "background.paper",
          borderRadius: 3,
          boxShadow: "0 1px 3px rgba(16,24,64,0.06), 0 2px 8px rgba(16,24,64,0.07)",
          width: "fit-content",
          mb: 3,
        }}
      >
        {tabItems.map(({ value, label, Icon }) => {
          const isActive = activeTab === value;
          return (
            <Box
              key={value}
              onClick={() => setActiveTab(value)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2.5,
                py: 1.1,
                borderRadius: 2,
                cursor: "pointer",
                userSelect: "none",
                bgcolor: isActive ? "primary.main" : "transparent",
                transition: "all 0.15s",
                "&:hover": {
                  bgcolor: isActive ? "primary.main" : alpha(theme.palette.primary.main, 0.06),
                },
              }}
            >
              <Icon sx={{ fontSize: "1rem", color: isActive ? "#fff" : "text.secondary" }} />
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: isActive ? 700 : 400,
                  color: isActive ? "#fff" : "text.secondary",
                  lineHeight: 1,
                }}
              >
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Tab content */}
      {activeTab === "discover" ? (
        <PublicCirclesTab />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <GroupSection
            title="Active"
            groups={myActiveGroups}
            accentColor={theme.palette.success.main}
            icon={<CheckCircleOutlineIcon sx={{ fontSize: "1rem" }} />}
            emptyMessage="No active circles yet. Join or create one to get started."
            onViewDetails={handleViewDetails}
            onJoinCircle={handleJoinCircle}
            onDeclineCircle={handleDeclineCircle}
          />
          <GroupSection
            title="Pending"
            groups={pendingGroups}
            accentColor={theme.palette.warning.main}
            icon={<AccessTimeIcon sx={{ fontSize: "1rem" }} />}
            emptyMessage="No pending invitations or circles waiting to start."
            onViewDetails={handleViewDetails}
            onJoinCircle={handleJoinCircle}
            onDeclineCircle={handleDeclineCircle}
          />
          <GroupSection
            title="Completed"
            groups={completedGroups}
            accentColor={theme.palette.primary.main}
            icon={<DoneAllIcon sx={{ fontSize: "1rem" }} />}
            emptyMessage="Completed circles will appear here."
            onViewDetails={handleViewDetails}
            onJoinCircle={handleJoinCircle}
            onDeclineCircle={handleDeclineCircle}
          />
        </Box>
      )}

      <GroupAgreementModal
        isOpen={open}
        onAgree={handleAgree}
        onClose={() => setOpen(false)}
        isVerified={isVerified}
        hasNoBankAccount={hasNoBankAccount ?? false}
      />
    </Box>
  );
};

export default ViewGroupImproved;
